import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import {
  filterEmptyValues,
  getDepartmentOptions,
  getFamiliesOptionsBySection,
  getSectionOptionsByDepartment,
} from 'utils/helpers'
import {
  Button,
  Dropdown,
  DurationInput,
  MultiSelectStoreDropdown,
  TextInput,
} from 'components/common'
import { MASTER_DATA_FORM_FIELDS } from 'utils/constants'
import { fetchMasterDataFreshCategories } from 'utils/api/masterDataApi'
import AggregatorsDropdown from './aggregatorsDropdown'

const {
  ITEM_NUMBER,
  ACTIVE_ONLINE,
  IS_AVAILABLE_IN_FRESH,
  IS_FRESH_PREPARATION,
  FRESH_CATEGORIES,
  BARCODE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_MARKETING_CODE,
  ERP_PACK_INFO,
  ERP_UOM,
  FAMILY,
  HIDE_AGGREGATORS,
  ACTIVATED_STORES,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  QTY_PER_UOM,
  SECTION,
  SUB_FAMILY,
  SAP_DEPARTMENT,
  SAP_SECTION,
  SAP_FAMILY,
  SAP_SUB_FAMILY,
  VAT_CLASS,
  VAT_PERCENTAGE,
  IS_LOOSE_ITEM,
  IS_SCALE_ITEM,
  IS_DISABLE,
  IS_THIRD_PARTY_PRODUCT,
  SAP_MATERIAL_NO,
  BLOCK_ERP_PRICE_SYNC,
  IS_BUNDLE_PRODUCT,
} = MASTER_DATA_FORM_FIELDS

type Props = {
  toggleFilterForm: () => void
}

export const FILTER_FIELD_MASTER_DATA = 'masterDataFilters'

const activeOnlineOptions = [
  { label: '--- NONE ---', value: 'NONE', disabled: false },
  { label: 'Yes', value: 'true', disabled: false },
  {
    label: 'No',
    value: 'false',
    disabled: false,
  },
]

const MasterDataFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_MASTER_DATA)

  const [resetFilter, setResetFilter] = useState(false)
  const [freshCategoryOptions, setFreshCategoryOptions] = useState<
    { label: string; value: string; disabled: boolean }[]
  >([])

  useEffect(() => {
    fetchMasterDataFreshCategories()
      .then(({ data }) =>
        setFreshCategoryOptions([
          { label: '--- NONE ---', value: 'NONE', disabled: false },
          ...(data?.categories ?? []).map(
            (category: { _id: string; name: string; isActive: boolean }) => ({
              // An inactive category still holds its item assignments, so it
              // stays selectable - just labelled so it is not mistaken for a
              // live one
              label: category.isActive
                ? category.name
                : `${category.name} (inactive)`,
              value: category._id,
              disabled: false,
            }),
          ),
        ]),
      )
      .catch(() => setFreshCategoryOptions([]))
  }, [])

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: filters,
  })

  const onFilterApply = (data: any) => {
    if (data?.dept === 'NONE') {
      delete data.dept
    }

    if (data?.section === 'NONE') {
      delete data.section
    }

    if (data?.family === 'NONE') {
      delete data.family
    }

    if (data?.sapDept === 'NONE') {
      delete data.sapDept
    }

    if (data?.sapSection === 'NONE') {
      delete data.sapSection
    }

    if (data?.sapFamily === 'NONE') {
      delete data.sapFamily
    }

    if (data?.activeOnline === 'NONE') {
      delete data.activeOnline
    }

    if (data?.isLooseItem === 'NONE') {
      delete data.isLooseItem
    }

    if (data?.isScaleItem === 'NONE') {
      delete data.isScaleItem
    }

    if (data?.blockPriceSync === 'NONE') {
      delete data.blockPriceSync
    }

    if (data?.isBundleProduct === 'NONE') {
      delete data.isBundleProduct
    }

    if (data?.freshPreparation === 'NONE') {
      delete data.freshPreparation
    }

    if (data?.isThirdPartyProduct === 'NONE') {
      delete data.isThirdPartyProduct
    }

    if (data?.isAvailableInFresh === 'NONE') {
      delete data.isAvailableInFresh
    }

    if (data?.freshCategories === 'NONE') {
      delete data.freshCategories
    }

    if (data?.isDisable === 'NONE') {
      delete data.isDisable
    }

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({ [FILTER_FIELD_MASTER_DATA]: filteredData, page: 1 })
    } else {
      removeQuery(FILTER_FIELD_MASTER_DATA)
    }

    toggleFilterForm()
  }

  // will watch the change of Department and Section Fields
  const deptWatch = watch('dept')
  const sectionWatch = watch('section')

  // Department Options
  const deptOptions = useMemo(() => getDepartmentOptions(), [])

  //  Section Options
  const sectionOptions = useMemo(() => {
    const dept = deptWatch || deptOptions[0]?.value

    return getSectionOptionsByDepartment(dept)
  }, [deptWatch, getSectionOptionsByDepartment])

  //  Family Options
  const familyOptions = useMemo(() => {
    const section = sectionWatch || sectionOptions[0]?.value

    return getFamiliesOptionsBySection(section)
  }, [sectionWatch, deptWatch, sectionOptions])

  // The SAP hierarchy cascades off the SAP lookup tables, so it needs its own
  // watches and option lists rather than sharing the legacy ones.
  const sapDeptWatch = watch(SAP_DEPARTMENT.key)
  const sapSectionWatch = watch(SAP_SECTION.key)

  const sapDeptOptions = useMemo(() => getDepartmentOptions(true), [])

  const sapSectionOptions = useMemo(() => {
    const dept = sapDeptWatch || sapDeptOptions[0]?.value

    return getSectionOptionsByDepartment(dept, true)
  }, [sapDeptWatch, sapDeptOptions])

  const sapFamilyOptions = useMemo(() => {
    const section = sapSectionWatch || sapSectionOptions[0]?.value

    return getFamiliesOptionsBySection(section, true)
  }, [sapSectionWatch, sapDeptWatch, sapSectionOptions])

  const cancelFilter = () => {
    reset(filters)
    setResetFilter((pre) => !pre)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3 ">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* Item Number */}
            <DurationInput
              register={register}
              startText="From"
              endText="To"
              title={ITEM_NUMBER.label}
            />

            {/* Item Number */}
            <DurationInput
              register={register}
              startText="From"
              endText="To"
              title={SAP_MATERIAL_NO.label}
            />

            {/* Barcode */}
            <TextInput
              register={register}
              name={BARCODE.key}
              label={BARCODE.label}
            />

            {/* Department */}
            <Dropdown
              name={DEPARTMENT.key}
              label={DEPARTMENT.label}
              options={deptOptions}
              setValue={setValue}
              defaultValue={filters?.dept || resetFilter}
            />

            {/* Section */}
            <Dropdown
              name={SECTION.key}
              label={SECTION.label}
              options={sectionOptions}
              setValue={setValue}
              defaultValue={filters?.section || sectionOptions?.[1]}
            />

            {/* Family */}
            <Dropdown
              name={FAMILY.key}
              label={FAMILY.label}
              options={familyOptions}
              setValue={setValue}
              defaultValue={filters?.family || familyOptions?.[1]}
            />

            {/* Sub Family */}
            <TextInput
              name={SUB_FAMILY.key}
              label={SUB_FAMILY.label}
              register={register}
            />

            {/* SAP Department */}
            <Dropdown
              name={SAP_DEPARTMENT.key}
              label={SAP_DEPARTMENT.label}
              options={sapDeptOptions}
              setValue={setValue}
              defaultValue={filters?.sapDept || resetFilter}
            />

            {/* SAP Section */}
            <Dropdown
              name={SAP_SECTION.key}
              label={SAP_SECTION.label}
              options={sapSectionOptions}
              setValue={setValue}
              defaultValue={filters?.sapSection || sapSectionOptions?.[1]}
            />

            {/* SAP Family */}
            <Dropdown
              name={SAP_FAMILY.key}
              label={SAP_FAMILY.label}
              options={sapFamilyOptions}
              setValue={setValue}
              defaultValue={filters?.sapFamily || sapFamilyOptions?.[1]}
            />

            {/* SAP Sub Family */}
            <TextInput
              name={SAP_SUB_FAMILY.key}
              label={SAP_SUB_FAMILY.label}
              register={register}
            />

            {/* ERP Item Description */}
            <TextInput
              name={ERP_ITEM_DESC.key}
              label={ERP_ITEM_DESC.label}
              register={register}
            />

            {/* ERP UOM */}
            <TextInput
              name={ERP_UOM.key}
              label={ERP_UOM.label}
              register={register}
            />

            {/* QTY ERP UOM */}
            <TextInput
              register={register}
              name={QTY_PER_UOM.key}
              label={QTY_PER_UOM.label}
            />

            {/* ERP Pack Info */}
            <TextInput
              register={register}
              name={ERP_PACK_INFO.key}
              label={ERP_PACK_INFO.label}
            />

            {/* ERP Brand */}
            <TextInput
              register={register}
              name={ERP_BRAND.key}
              label={ERP_BRAND.label}
            />

            {/* ERP Marketing Code */}
            <TextInput
              register={register}
              name={ERP_MARKETING_CODE.key}
              label={ERP_MARKETING_CODE.label}
            />

            {/* Hide Aggregators */}
            <AggregatorsDropdown
              defaultSelected={filters?.[HIDE_AGGREGATORS.key]}
              resetForm={resetFilter}
              setValue={setValue}
              name={HIDE_AGGREGATORS.key}
              label={HIDE_AGGREGATORS.label}
            />

            {/* Activated Stores */}
            <MultiSelectStoreDropdown
              defaultSelected={filters?.[ACTIVATED_STORES.key]}
              resetForm={resetFilter}
              setValue={setValue}
              name={ACTIVATED_STORES.key}
              label={ACTIVATED_STORES.label}
            />

            {/* Online Description */}
            <TextInput
              register={register}
              name={ONLINE_DESC.key}
              label={ONLINE_DESC.label}
            />

            {/* Online Pack Info */}
            <TextInput
              register={register}
              name={ONLINE_PACK_INFO.key}
              label={ONLINE_PACK_INFO.label}
            />

            {/* Active Online */}
            <Dropdown
              name={ACTIVE_ONLINE.key}
              label={ACTIVE_ONLINE.label}
              setValue={setValue}
              defaultValue={filters?.[ACTIVE_ONLINE.key] || resetFilter}
              options={activeOnlineOptions}
            />

            <Dropdown
              name={IS_AVAILABLE_IN_FRESH.key}
              label={IS_AVAILABLE_IN_FRESH.label}
              setValue={setValue}
              defaultValue={filters?.[IS_AVAILABLE_IN_FRESH.key] || resetFilter}
              options={activeOnlineOptions}
            />

            <Dropdown
              name={IS_FRESH_PREPARATION.key}
              label={IS_FRESH_PREPARATION.label}
              setValue={setValue}
              defaultValue={filters?.[IS_FRESH_PREPARATION.key] || resetFilter}
              options={activeOnlineOptions}
            />

            <Dropdown
              name={FRESH_CATEGORIES.key}
              label={FRESH_CATEGORIES.label}
              setValue={setValue}
              defaultValue={filters?.[FRESH_CATEGORIES.key] || resetFilter}
              options={freshCategoryOptions}
            />

            <Dropdown
              name={IS_LOOSE_ITEM.key}
              label={IS_LOOSE_ITEM.label}
              setValue={setValue}
              defaultValue={filters?.[IS_LOOSE_ITEM.key] || resetFilter}
              options={activeOnlineOptions}
            />

            <Dropdown
              name={IS_SCALE_ITEM.key}
              label={IS_SCALE_ITEM.label}
              setValue={setValue}
              defaultValue={filters?.[IS_SCALE_ITEM.key] || resetFilter}
              options={activeOnlineOptions}
            />

            {/* Vat Class */}
            <TextInput
              register={register}
              name={VAT_CLASS.key}
              label={VAT_CLASS.label}
            />

            {/* Vat Percentage */}
            <TextInput
              register={register}
              name={VAT_PERCENTAGE.key}
              label={VAT_PERCENTAGE.label}
            />

            <Dropdown
              name={IS_DISABLE.key}
              label={IS_DISABLE.label}
              setValue={setValue}
              defaultValue={filters?.[IS_DISABLE.key] || resetFilter}
              options={activeOnlineOptions}
            />
            <Dropdown
              name={BLOCK_ERP_PRICE_SYNC.key}
              label={BLOCK_ERP_PRICE_SYNC.label}
              setValue={setValue}
              defaultValue={filters?.[BLOCK_ERP_PRICE_SYNC.key] || resetFilter}
              options={activeOnlineOptions}
            />

            <Dropdown
              name={IS_THIRD_PARTY_PRODUCT.key}
              label={IS_THIRD_PARTY_PRODUCT.label}
              setValue={setValue}
              defaultValue={
                filters?.[IS_THIRD_PARTY_PRODUCT.key] || resetFilter
              }
              options={[
                { label: '--- NONE ---', value: 'NONE', disabled: false },
                { label: 'Yes', value: 'true', disabled: false },
                {
                  label: 'No',
                  value: 'false',
                  disabled: false,
                },
              ]}
            />

            <Dropdown
              name={IS_BUNDLE_PRODUCT.key}
              label={IS_BUNDLE_PRODUCT.label}
              setValue={setValue}
              defaultValue={filters?.[IS_BUNDLE_PRODUCT.key] || resetFilter}
              options={activeOnlineOptions}
            />
          </div>
          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />

            <Button
              buttonText="cancel"
              variant="outline"
              type="button"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default MasterDataFilterForm
